import React, { useState, useEffect } from "react";
import { Routes, Route, BrowserRouter, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, actions } from "./state/reducer";
import "./scss/app.scss";
import SignupPage from "./pages/signup";
import HomePage from "./pages/home";
import WatchProfilePage from "./pages/profile/watch";
import EditProfilePage from "./pages/profile/edit";
import PreloaderPage from "./pages/preloader";
import { backend } from "./services/backend";
import SigninPage from "./pages/signin";
import ErrorPage from "./pages/error";
import RecoverPage from "./pages/auth/recoverPassword";
import UserListPage from "./pages/users/users";
import GroupListPage from "./pages/users/groups";
import RoleListPage from "./pages/users/roles";
import TasksListPage from "./pages/activity/tasks";
import ClientsListPage from "./pages/activity/clients";
import EventsListPage from "./pages/activity/events";
import EmailConfirmPage from "./pages/auth/confirmEmail";
import NewPasswordPage from "./pages/auth/newPassword";
import ClientsDetailPage from "./pages/detail/client";
import EventDetailPage from "./pages/detail/event";
import ProtectedRoute from "./protected";
import TaskDetailPage from "./pages/detail/task";
import { userBackend } from "./services";
import { toast } from "sonner";
import InviteListPage from "./pages/users/invites";
import SourcesListPage from "./pages/activity/sources";
import SalesPlanPage from "./pages/analytics/salesPlan";
import SalesFunnelPage from "./pages/analytics/salesFunnel";
import OfficeRatingPage from "./pages/analytics/officeRating";
import PeopleRatingPage from "./pages/analytics/peopleRating";
import { defaultCustomerFilters } from "./forms/filters/customers";
import { defaultEventFilters } from "./forms/filters/event";
import { defaultTaskFilters } from "./forms/filters/task";
import { generateRandomString } from "./utils";
import SalesGroupPlanPage from "./pages/analytics/salesPlanGroup";
import {
  closeHotInterval,
  refreshInterval,
  refreshNotifications,
} from "./components/notifications";

const App = () => {
  const [isAuth, setAuth] = useState(false);
  const [isConfirmed, setConfirmed] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loaded, setIsLoaded] = useState(false);
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();

  const session = State.session;
  const rights = State.rights;

  useEffect(() => {
    backend
      .getSessionData()
      .then((res) => {
        dispatch(actions.saveSession(res));
        setAuth(!!res.id);
        setConfirmed(!!res.is_confirmed);
        setIsBlocked(!!res.is_blocked);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (isAuth) {
      // refreshNotifications();
      setTimeout(refreshNotifications, closeHotInterval);
      setInterval(refreshNotifications, refreshInterval);
    }
  }, [isAuth]);

  useEffect(() => {
    if (!session) return;
    userBackend
      .getUserFiles(session.id)
      .then((res) => {
        dispatch(actions.currentUserPhotos(res.files));
      })
      .catch((e) => {
        console.log(e);
      });
    userBackend
      .getUserProfile(session.id)
      .then((res) => {
        console.log("Res: ", res);
        if (!res || !res.data.first_name) {
          toast("Заполните профиль, чтобы пользоваться системой");
        }
        if (res) {
          // Устанавливаем значения в форму
          dispatch(actions.currentUserProfile(res.data));
        }
      })
      .catch((e) => {
        console.error("Ошибка загрузки данных пользователя:", e.message);
      });
    backend
      .getCurrentUserRightsData()
      .then((res) => {
        if (res.rights) {
          dispatch(actions.rights(res.rights));
          const startFilters = State.userListFilters;
          const defaultUserFiltersWithRights = {
            ...startFilters,
            group_ids: res.rights?.group_ids || [],
          };
          const defaultEventsFiltersWithRights = (() => {
            switch (true) {
              case rights.rang < 3 || rights.permissions.indexOf(2) > -1:
                return {
                  ...defaultEventFilters,
                  user_id: 0,
                  group_id: 0,
                };
              case rights.permissions.indexOf(1) > -1:
                return {
                  ...defaultEventFilters,
                  user_id: 0,
                  group_id: rights.group_ids[0],
                };
              default:
                return { ...defaultEventFilters, user_id: session.id };
            }
          })();
          const defaultCustomerFiltersWithRights = (() => {
            switch (true) {
              case rights.rang < 3 || rights.permissions.indexOf(11) > -1:
                return {
                  ...defaultCustomerFilters,
                  user_id: 0,
                  group_id: 0,
                };
              case rights.permissions.indexOf(10) > -1:
                return {
                  ...defaultCustomerFilters,
                  user_id: 0,
                  group_id: rights.group_ids[0],
                };
              default:
                return { ...defaultCustomerFilters, user_id: session.id };
            }
          })();

          const defaultTaskFiltersWithRights = (() => {
            switch (true) {
              case rights.rang < 3 || rights.permissions.indexOf(6) > -1:
                return {
                  ...defaultTaskFilters,
                  user_id: 0,
                  group_id: 0,
                };
              case rights.permissions.indexOf(5) > -1:
                return {
                  ...defaultTaskFilters,
                  user_id: 0,
                  group_id: rights.group_ids[0],
                };
              default:
                return { ...defaultTaskFilters, user_id: session.id };
            }
          })();
          dispatch(actions.userRequestParams(defaultUserFiltersWithRights));
          dispatch(actions.selectItem(generateRandomString(3)));
          dispatch(
            actions.customerRequestParams(defaultCustomerFiltersWithRights)
          );
          dispatch(actions.taskRequestParams(defaultTaskFiltersWithRights));
          if (document.location.pathname.indexOf("client") === -1)
            dispatch(
              actions.eventRequestParams(defaultEventsFiltersWithRights)
            );
        }
      })
      .catch((e) => {
        console.error("Ошибка загрузки прав пользователя:", e.message);
      });
  }, [session]);

  if (!loaded) {
    return <PreloaderPage />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<SigninPage />} />
        <Route path="/signup/:id" element={<SignupPage />} />
        <Route path="/recover" element={<RecoverPage />} />
        <Route path="/recoverpassword" element={<NewPasswordPage />} />
        <Route path="/signupconfirm" element={<EmailConfirmPage />} />
        <Route
          path="/noemail"
          element={
            <ErrorPage
              code={403}
              message={
                "Для доступа к системе подтвердите адрес электронной почты"
              }
              link={{
                name: "Отправить повторно",
                action: () => (document.location.href = "/signupconfirm"),
              }}
            />
          }
        />
        <Route
          path="/423"
          element={
            <ErrorPage
              code={423}
              message={
                "Ваша учетная запись была заблокирована вышестоящим пользователем"
              }
            />
          }
        />
        <Route
          path="/403"
          element={
            <ErrorPage
              code={403}
              message={"Доступ не разрешен для Вашего уровня полномочий"}
            />
          }
        />
        <Route
          element={
            <ProtectedRoute
              isAuth={isAuth}
              isConfirmed={isConfirmed}
              isBlocked={isBlocked}
            />
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/preloader" element={<PreloaderPage />} />
          <Route path="/users/:page" element={<UserListPage />} />
          <Route path="/groups/:page" element={<GroupListPage />} />
          <Route path="/tasks/:page" element={<TasksListPage />} />
          <Route path="/events/:page" element={<EventsListPage />} />
          <Route path="/clients/sources" element={<SourcesListPage />} />
          <Route path="/clients/:page" element={<ClientsListPage />} />
          <Route path="/client/:clientid" element={<ClientsDetailPage />} />
          <Route path="/events/detail/:eventid" element={<EventDetailPage />} />
          <Route path="/tasks/detail/:taskid" element={<TaskDetailPage />} />
          <Route path="/analytics" element={<SalesPlanPage />} />
          <Route path="/analytics/salesplan" element={<SalesPlanPage />} />
          <Route
            path="/analytics/groupsplan"
            element={<SalesGroupPlanPage />}
          />
          <Route path="/analytics/salesfunnel" element={<SalesFunnelPage />} />
          <Route path="/analytics/officerating" element={<OfficeRatingPage />} />
          <Route
            path="/analytics/peoplerating"
            element={<PeopleRatingPage />}
          />
          <Route path="/roles/:page" element={<RoleListPage />} />
          <Route path="/profile" element={<WatchProfilePage />} />
          <Route path="/profile/edit" element={<EditProfilePage />} />
          <Route path="/profile/edit/:id" element={<EditProfilePage />} />
          <Route path="/profile/:id" element={<WatchProfilePage />} />
          <Route path="/users/invites" element={<InviteListPage />} />
        </Route>
        <Route
          path="*"
          element={<ErrorPage code={404} message={"Путь не найден"} />}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
